import { photo, photoWide, photoWideSrcSet, photoCardSrcSet, mediaSlots, nativeWideWidth } from "@/lib/media";

/** Wide header image for a service, never upscaled past the photo's native width. */
const serviceHero = (slot: number) => photoWide(slot, nativeWideWidth[slot] ?? 1920);

/** Matching candidate widths, so the header is not over-fetched on small screens. */
const serviceHeroSrcSet = (slot: number) => photoWideSrcSet(slot, nativeWideWidth[slot] ?? 1920);

export interface ServiceData {
  slug: string;
  title: string;
  subtitle: string;
  price: string;
  /** 4:3 card thumbnail. */
  image: string;
  /** Candidate widths for the card, so phones do not fetch the 800px render. */
  imageSrcSet: string;
  /** Wide 16:9 variant for the service page header — the card image upscales badly there. */
  heroImage: string;
  /** Candidate widths for the header, capped at each photo's native width. */
  heroImageSrcSet: string;
  description: string;
  features: string[];
  process: { step: string; desc: string }[];
}

export const services: ServiceData[] = [
  {
    slug: "interior-painting",
    title: "Interior Painting",
    subtitle: "Walls, ceilings, trim & cabinets",
    price: "Free Estimate",
    image: photo(mediaSlots.services[0]),
    imageSrcSet: photoCardSrcSet(mediaSlots.services[0]),
    heroImage: serviceHero(mediaSlots.services[0]),
    heroImageSrcSet: serviceHeroSrcSet(mediaSlots.services[0]),
    description:
      "Clean, straight lines and a finish that lasts. E & J Painting handles interior repaints across Middle Tennessee — living rooms, bedrooms, kitchens, ceilings, trim, doors, and accent walls. We mask and cover everything before a drop of paint is opened, and we leave your home cleaner than we found it.",
    features: [
      "Walls, ceilings, trim, doors & baseboards",
      "Accent walls and full-home color changes",
      "Full floor and furniture protection",
      "Patching, caulking & surface prep included",
      "Low-VOC and low-odor paint options",
      "Clean, crisp cut-in lines — no tape marks",
    ],
    process: [
      { step: "Walkthrough", desc: "We measure each room and confirm colors, sheens, and problem areas." },
      { step: "Written Quote", desc: "You get a clear, itemized price up front — no surprise charges later." },
      { step: "Prep & Protect", desc: "Furniture is moved and covered, floors masked, and surfaces patched and sanded." },
      { step: "Paint & Inspect", desc: "Two coats where needed, then we walk every room with you before we pack up." },
    ],
  },
  {
    slug: "exterior-painting",
    title: "Exterior Painting",
    subtitle: "Siding, trim, soffits & fascia",
    price: "Free Estimate",
    image: photo(mediaSlots.services[1]),
    imageSrcSet: photoCardSrcSet(mediaSlots.services[1]),
    heroImage: serviceHero(mediaSlots.services[1]),
    heroImageSrcSet: serviceHeroSrcSet(mediaSlots.services[1]),
    description:
      "Tennessee sun, humidity, and storms are hard on a house. Our exterior painting service protects your siding and trim with proper prep, quality exterior-grade coatings, and careful attention to the details most crews rush — soffits, fascia, shutters, and window trim included.",
    features: [
      "Wood, vinyl, fiber cement & stucco siding",
      "Trim, soffits, fascia, shutters & front doors",
      "Pressure washing and surface cleaning first",
      "Scraping, sanding, priming & caulking",
      "Weather-rated exterior coatings",
      "Decks, porches, railings & garage doors",
    ],
    process: [
      { step: "Site Visit", desc: "We inspect siding, trim, and any rot or peeling before quoting." },
      { step: "Wash & Prep", desc: "Surfaces are washed, scraped, sanded, primed, and caulked as needed." },
      { step: "Coat & Cover", desc: "Windows, roofing, and landscaping are masked before spraying or brushing." },
      { step: "Final Walk", desc: "We review every elevation with you and touch up anything you point out." },
    ],
  },
  {
    slug: "residential-painting",
    title: "Residential Painting",
    subtitle: "Whole-home painting for homeowners",
    price: "Free Estimate",
    image: photo(mediaSlots.services[2]),
    imageSrcSet: photoCardSrcSet(mediaSlots.services[2]),
    heroImage: serviceHero(mediaSlots.services[2]),
    heroImageSrcSet: serviceHeroSrcSet(mediaSlots.services[2]),
    description:
      "Whether you just closed on a house in Murfreesboro, are prepping a home to sell in Franklin, or finally want that dated color gone, we handle residential painting from start to finish. Single rooms, whole interiors, full exteriors, new construction, and rental turnovers.",
    features: [
      "Whole-home interior & exterior repaints",
      "New construction & new-build painting",
      "Move-in, move-out & rental turnovers",
      "Color consultation and sample walls",
      "Popcorn ceiling removal & refinishing",
      "Respectful crews and tidy job sites",
    ],
    process: [
      { step: "Free Estimate", desc: "We visit your home, take measurements, and listen to what you want." },
      { step: "Plan & Schedule", desc: "We lock in colors, a start date, and a realistic completion window." },
      { step: "The Work", desc: "Our crew shows up on time, preps thoroughly, and paints to spec." },
      { step: "Your Approval", desc: "Nothing is final until you've walked the job and signed off on it." },
    ],
  },
  {
    slug: "commercial-painting",
    title: "Commercial Painting",
    subtitle: "Offices, retail, warehouses & more",
    price: "Free Estimate",
    image: photo(mediaSlots.services[3]),
    imageSrcSet: photoCardSrcSet(mediaSlots.services[3]),
    heroImage: serviceHero(mediaSlots.services[3]),
    heroImageSrcSet: serviceHeroSrcSet(mediaSlots.services[3]),
    description:
      "Keep your business looking sharp without shutting it down. E & J Painting schedules commercial work around your hours — evenings and weekends included — so offices, storefronts, restaurants, warehouses, and apartment complexes get repainted with minimal disruption to your operation.",
    features: [
      "Offices, retail spaces & restaurants",
      "Warehouses, shops & industrial coatings",
      "Apartment complexes & property management",
      "After-hours and weekend scheduling",
      "Common areas, hallways & stairwells",
      "Clear timelines and single point of contact",
    ],
    process: [
      { step: "Walkthrough", desc: "We tour the property, note square footage, surfaces, and access hours." },
      { step: "Detailed Bid", desc: "You receive a written scope and price you can hand to ownership." },
      { step: "Off-Hours Work", desc: "We paint around your business so your doors stay open." },
      { step: "Punch List", desc: "We close out with a walkthrough and knock out every punch-list item." },
    ],
  },
  {
    slug: "drywall-painting",
    title: "Drywall & Painting",
    subtitle: "Repair, patching, texture & finish",
    price: "Free Estimate",
    image: photo(mediaSlots.services[4]),
    imageSrcSet: photoCardSrcSet(mediaSlots.services[4]),
    heroImage: serviceHero(mediaSlots.services[4]),
    heroImageSrcSet: serviceHeroSrcSet(mediaSlots.services[4]),
    description:
      "Paint only looks as good as the surface underneath it. We repair damaged drywall, patch holes and cracks, match existing texture, and finish it all with a seamless coat of paint — so the repair disappears instead of standing out.",
    features: [
      "Hole, crack & water damage repair",
      "Drywall hanging, taping & mudding",
      "Texture matching (knockdown, orange peel, smooth)",
      "Nail pops, seams & corner bead repair",
      "Ceiling repair and refinishing",
      "Sanded, primed & painted to blend in",
    ],
    process: [
      { step: "Assess Damage", desc: "We find the cause first — a patch over an active leak won't hold." },
      { step: "Repair & Tape", desc: "Damaged board is cut out, replaced, taped, and mudded." },
      { step: "Texture Match", desc: "We match the existing texture so the patch blends into the wall." },
      { step: "Prime & Paint", desc: "The area is primed and painted so the repair is invisible." },
    ],
  },
];
